import type {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Propuestas} from '@/components/perfil-propuestas'
import {Tag} from '@/components/ui/tag'
import {DataTable, type Column} from '@/components/data-table'
import {OrigenLinea} from '@/components/origen-linea'
import type {PerfilBloqueRead, PerfilLineaRead} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {CabeceraDeBloque} from '@/components/perfil-bloque-cabecera'
import {TIPOS_COMPETENCIA, TIPOS_REQUISITO, type Apertura, type Bloque} from '@/lib/perfil-bloques'

export type AccionesDeLinea = {
  editable: boolean
  pending: boolean
  desplegado: boolean
  businessId: string | undefined
  onAdoptar: (linea: PerfilLineaRead) => void
  onAdoptarTodas: (lineas: PerfilLineaRead[]) => void
  onQuitar: (linea: PerfilLineaRead) => void
  onConfirmarLinea: (linea: PerfilLineaRead) => void
  onEditar: (linea: PerfilLineaRead) => void
  /** Abrir el diálogo de línea nueva en un bloque, o de una medida bajo un peligro (KAI-144). */
  onAgregar: (apertura: Apertura) => void
}

/** Las columnas por tipo de cada bloque; un bloque sin entrada es una sola columna. */
const COLUMNAS_POR_TIPO: Partial<Record<string, readonly string[]>> = {
  requerimientos: TIPOS_REQUISITO,
  competencias: TIPOS_COMPETENCIA,
}

export function PerfilBloqueTabla({
  bloque,
  ayuda,
  ...acciones
}: {bloque: PerfilBloqueRead; ayuda?: ReactNode} & AccionesDeLinea) {
  const {t} = useTranslation()
  const {editable, pending, onAdoptar, onAdoptarTodas, onAgregar} = acciones
  const tipos = COLUMNAS_POR_TIPO[bloque.bloque]
  const item = t(`perfilCargo.tabla.item.${bloque.bloque}`)

  return (
    <section className="surface rounded-lg p-4" data-testid={`bloque-${bloque.bloque}`}>
      <CabeceraDeBloque
        bloque={bloque.bloque as Bloque}
        ayuda={ayuda}
        editable={editable}
        pending={pending}
        onAgregar={(abierto) => onAgregar({modo: 'nueva', bloque: abierto})}
      />
      {bloque.lineas.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground" data-testid={`bloque-vacio-${bloque.bloque}`}>
          {t('perfilCargo.detalle.sinLineas')}
        </p>
      ) : tipos ? (
        <TablaPorTipos bloque={bloque} tipos={tipos} {...acciones} />
      ) : (
        <div className="mt-2">
          <DataTable
            columns={columnasGuardadas(item, acciones)}
            rows={bloque.lineas}
            rowKey={(linea) => linea.clave}
            onRowClick={editable ? acciones.onEditar : undefined}
          />
        </div>
      )}

      <Propuestas
        bloque={bloque.bloque}
        propuestas={bloque.propuestas}
        editable={editable}
        pending={pending}
        onAdoptarTodas={onAdoptarTodas}>
        <div className="mt-1">
          <DataTable
            columns={columnasPropuestas(item, !!tipos, t, editable, pending, onAdoptar)}
            rows={bloque.propuestas}
            rowKey={(linea) => linea.clave}
            rowTestId={(linea) => `propuesta-${linea.clave}`}
          />
        </div>
      </Propuestas>
    </section>
  )
}

type T = (key: string, opts?: Record<string, unknown>) => string

function Item({linea, acciones, conTipo}: {linea: PerfilLineaRead; acciones: AccionesDeLinea; conTipo: boolean}) {
  const {t} = useTranslation()
  const {editable, pending, desplegado, businessId, onQuitar, onConfirmarLinea} = acciones
  return (
    <div className="space-y-1" data-testid={`linea-${linea.clave}`}>
      <span className="whitespace-pre-wrap">{linea.texto}</span>
      <div className="flex flex-wrap gap-1">
        {linea.confirmado ? (
          <Tag tone="green">{t('perfilCargo.estado.confirmado')}</Tag>
        ) : (
          <Tag tone="amber">{t('perfilCargo.estado.borrador')}</Tag>
        )}
        {!linea.texto.trim() && <Tag tone="amber">{t('perfilCargo.tabla.sinDescripcion')}</Tag>}
        {conTipo && !linea.subtipo && <Tag tone="amber">{t('perfilCargo.tabla.sinTipo')}</Tag>}
      </div>
      <OrigenLinea linea={linea} businessId={businessId} desplegado={desplegado} />
      {editable && (
        <div className="flex flex-wrap gap-2">
          {!linea.confirmado && (
            <Button size="sm" variant="outline" disabled={pending} onClick={() => onConfirmarLinea(linea)}>
              {t('perfilCargo.detalle.confirmarLinea')}
            </Button>
          )}
          <Button size="sm" variant="ghost" disabled={pending} onClick={() => onQuitar(linea)}>
            {t('perfilCargo.detalle.quitar')}
          </Button>
          <Button size="sm" variant="ghost" disabled={pending} onClick={() => acciones.onEditar(linea)}>
            {t('perfilCargo.tabla.editar')}
          </Button>
        </div>
      )}
    </div>
  )
}

/** Una sola fila con una columna por tipo; las líneas sin tipo caen en la última, con su chip. */
function TablaPorTipos({
  bloque,
  tipos,
  ...acciones
}: {bloque: PerfilBloqueRead; tipos: readonly string[]} & AccionesDeLinea) {
  const {t} = useTranslation()
  const ultimo = tipos[tipos.length - 1]
  const columnas: Column<{id: string}>[] = tipos.map((tipo) => ({
    key: tipo,
    header: t(`perfilCargo.subtipo.${tipo}`),
    className: 'align-top',
    cell: () => (
      <div className="space-y-3" data-testid={`${bloque.bloque}-${tipo}`}>
        {bloque.lineas
          .filter((linea) => (tipos.includes(linea.subtipo ?? '') ? linea.subtipo : ultimo) === tipo)
          .map((linea) => (
            <Item key={linea.clave} linea={linea} acciones={acciones} conTipo />
          ))}
      </div>
    ),
  }))
  return (
    <div className="mt-2">
      <DataTable columns={columnas} rows={[{id: bloque.bloque}]} rowKey={(fila) => fila.id} />
    </div>
  )
}

function columnasGuardadas(item: string, acciones: AccionesDeLinea): Column<PerfilLineaRead>[] {
  return [
    {
      key: 'item',
      header: item,
      cell: (linea) => <Item linea={linea} acciones={acciones} conTipo={false} />,
    },
  ]
}

function columnasPropuestas(
  item: string,
  conTipo: boolean,
  t: T,
  editable: boolean,
  pending: boolean,
  onAdoptar: (linea: PerfilLineaRead) => void
): Column<PerfilLineaRead>[] {
  const columnas: Column<PerfilLineaRead>[] = [
    {
      key: 'item',
      header: item,
      width: 'w-2/5',
      cell: (linea) => <span className="whitespace-pre-wrap">{linea.texto}</span>,
    },
  ]
  if (conTipo) {
    columnas.push({
      key: 'tipo',
      header: t('perfilCargo.tabla.tipo'),
      width: 'w-32',
      cell: (linea) => (linea.subtipo ? t(`perfilCargo.subtipo.${linea.subtipo}`) : ''),
    })
  }
  columnas.push({
    key: 'acto',
    header: '',
    width: 'w-40',
    stopClick: true,
    cell: (linea) =>
      editable ? (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" disabled={pending} onClick={() => onAdoptar(linea)}>
            {t('perfilCargo.detalle.adoptar')}
          </Button>
        </div>
      ) : null,
  })
  return columnas
}
